import React from 'react';
import PlanUnitSelector from 'components/shared/PlanUnitSelector';
import { StyledActionStripBox } from './Toolbar.styles';
import ToolbarAction from './ToolbarAction';
import ToolbarActionMenu from './ToolbarActionMenu';
import { StyledBoxFlexAlignCenter } from 'components/shared/Box/Box.styles';
import VerticalToolbarActionWrapper from './VerticalToolbarActionWrapper';
import { useIsVerticalToolbar } from '../ToolbarPosition.context';
import { t } from 'i18next';

// types
import type { ActionStripProps } from './ActionStrip.types';
import type { ToolbarAction as ToolbarActionType } from '../ToolbarSet.types';

export const ActionStrip = ({
  items,
  justifyEnd,
  children,
  planUnitSelector
}: ActionStripProps) => {
  const isVertical = useIsVerticalToolbar();

  const renderAction = (action: ToolbarActionType) => (
    <React.Fragment key={action.id}>
      {action?.contextMenu?.length ? (
        <ToolbarActionMenu action={action} />
      ) : (
        <ToolbarAction action={action} />
      )}
    </React.Fragment>
  );

  // Everything the screen passed goes straight to the selector; the toolbar only adds the two
  // props that are its own layout state, and they go *after* the spread and must stay there: the
  // type keeps them out of what a screen can pass, but that is only enforced for fresh object
  // literals, so the ordering is what actually stops a screen overriding the toolbar's layout.
  // Guarded on onSelected rather than on the object: these screens are rendered through the Dojo
  // bridge with untyped props, and the selector calls onSelected without checking it first.
  const planUnitSelectorElement = planUnitSelector?.onSelected && (
    <PlanUnitSelector<boolean, boolean>
      {...planUnitSelector}
      isToolbar
      isVertical={isVertical}
    />
  );

  return (
    <StyledActionStripBox
      component="div"
      isVertical={isVertical}
      justifyEnd={justifyEnd}
    >
      {planUnitSelectorElement && (
        <StyledBoxFlexAlignCenter>
          {/* Docked, the 550px selector does not fit beside the icons, so it collapses into a
                popover behind one of them — a different interaction model, not different styling. */}
          {isVertical ? (
            <VerticalToolbarActionWrapper
              iconType="grid-list"
              title={t('toolbar.selectPlanUnit')}
              divider={{ bottom: true }}
            >
              {planUnitSelectorElement}
            </VerticalToolbarActionWrapper>
          ) : (
            planUnitSelectorElement
          )}
        </StyledBoxFlexAlignCenter>
      )}
      {items?.map(renderAction)}
      {children}
    </StyledActionStripBox>
  );
};
